import { useState, useCallback, useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Modal,
  Platform,
} from 'react-native';
import { useNavigation, useRoute, RouteProp, useFocusEffect } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import Svg, { Path } from 'react-native-svg';
import * as XLSX from 'xlsx';
import { COLORS, FONTS } from '../../constants/config';
import { PageContainer, Navbar, Breadcrumb } from '../../components';
import { GoalsSkeleton } from '../../components/Skeleton';
import { useRoleNavigation } from '../../hooks';
import { employeesApi, organizationalUnitsApi } from '../../services/apiService';
import { useEmployeesQuery } from '../../hooks/queries';
import { queryKeys } from '../../providers/QueryProvider';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useYear } from '../../contexts/YearContext';
import { Employee } from '../../types';

type NavigationProp = NativeStackNavigationProp<any>;
type RouteProps = RouteProp<{ Employees: { organizationId?: number; organizationName?: string } }, 'Employees'>;

// Icons
const PlusIcon = ({ size = 16, color = '#FFFFFF' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 5V19M5 12H19" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const ChevronDownIcon = ({ size = 14, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M6 9L12 15L18 9" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const ChevronLeftIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M15 18L9 12L15 6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const ChevronRightIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M9 18L15 12L9 6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const XIcon = ({ size = 14, color = '#EF4444' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M18 6L6 18M6 6L18 18" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const EyeIcon = ({ size = 18, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M1 12C1 12 5 4 12 4C19 4 23 12 23 12C23 12 19 20 12 20C5 20 1 12 1 12Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const EditIcon = ({ size = 18, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M18.5 2.50001C18.8978 2.10219 19.4374 1.87869 20 1.87869C20.5626 1.87869 21.1022 2.10219 21.5 2.50001C21.8978 2.89784 22.1213 3.4374 22.1213 4.00001C22.1213 4.56262 21.8978 5.10219 21.5 5.50001L12 15L8 16L9 12L18.5 2.50001Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const TrashIcon = ({ size = 18, color = '#EF4444' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M3 6H5H21" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const UploadIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M17 8L12 3L7 8" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 3V15" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const DownloadIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M7 10L12 15L17 10" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 15V3" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const CheckIcon = ({ size = 16, color = '#22C55E' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M20 6L9 17L4 12" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

export default function EmployeesScreen() {
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute<RouteProps>();
  const organizationId = route.params?.organizationId;
  const organizationName = route.params?.organizationName;
  const { isViewOnly, isOrgEmployee, navigateToHome } = useRoleNavigation();

  // الموظف (Section Employee) has no access to the employees directory — only
  // رئيس القسم (Section Head) and cross-department readers do. Guard the route
  // itself so a direct URL / deep link can't open it (the backend also 403s).
  useEffect(() => {
    if (isOrgEmployee) {
      navigateToHome();
    }
  }, [isOrgEmployee, navigateToHome]);

  const { colors, isDark } = useTheme();
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';
  const rtlRow = isRTL ? 'row-reverse' as const : 'row' as const;
  const { selectedYear } = useYear();

  // Theme colors
  const themeColors = {
    card: colors.card,
    cardBorder: colors.cardBorder,
    textPrimary: colors.textPrimary,
    textSecondary: colors.textMuted,
    sectionBg: isDark ? colors.backgroundAlt : '#F8FAFC',
    border: colors.border,
    tableHeader: isDark ? colors.backgroundAlt : '#F8FAFC',
    tableRow: colors.card,
    tableRowAlt: isDark ? colors.backgroundAlt : '#FAFAFA',
  };

  // Filters
  const [filterDepartment, setFilterDepartment] = useState<string>('all');
  const [filterType, setFilterType] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [showDepartmentDropdown, setShowDepartmentDropdown] = useState(false);
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  // Delete modal state
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [employeeToDelete, setEmployeeToDelete] = useState<Employee | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Feedback message state
  const [feedbackMessage, setFeedbackMessage] = useState<{ type: 'success' | 'info' | 'error'; text: string } | null>(null);

  const queryClient = useQueryClient();

  // Employees main list — React Query renders cached data instantly on focus.
  // Filter changes flow through the query key and trigger a background refetch.
  const employeeQueryParams = useMemo(() => {
    const params: any = {
      page: currentPage,
      pageSize,
      department_id: organizationId,
      year: selectedYear,
      includeSso: true,  // list the org's SSO directory (matches the جهتي headcount card)
    };
    if (filterDepartment !== 'all') params.organizational_unit = Number(filterDepartment);
    if (filterType !== 'all') params.employee_type = filterType;
    if (filterStatus !== 'all') params.is_active = filterStatus === 'active';
    return params;
  }, [currentPage, filterDepartment, filterType, filterStatus, organizationId, selectedYear]);

  const {
    data: employeesResponse,
    isLoading: loading,
    refetch,
  } = useEmployeesQuery(employeeQueryParams);

  const employees: Employee[] = useMemo(
    () => employeesResponse?.results || [],
    [employeesResponse],
  );
  const totalCount = employeesResponse?.count || 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  // Organizational units (for the filter dropdown). Separate query, rarely changes.
  const { data: unitsDropdownData } = useQuery({
    queryKey: ['organizationalUnits', 'dropdown', organizationId],
    queryFn: async () => {
      const params: Record<string, any> = { page_size: 100 };
      if (organizationId) params.department_id = organizationId;
      return organizationalUnitsApi.list(params);
    },
    staleTime: 10 * 60 * 1000,
  });
  const departments = useMemo(
    () =>
      (unitsDropdownData?.results || []).map((unit: any) => ({
        id: unit.id,
        name: unit.name_ar || unit.name,
      })),
    [unitsDropdownData],
  );

  // Refresh on focus — cached data renders instantly, refetch runs silently.
  useFocusEffect(
    useCallback(() => {
      refetch();
    }, [refetch])
  );

  // Reset to page 1 when year changes; query key change triggers a refetch.
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedYear]);

  // Back-compat shim for handlers that called fetchData().
  const fetchData = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: queryKeys.employees.all });
  }, [queryClient]);

  const applyFilters = () => {
    setShowDepartmentDropdown(false);
    setShowTypeDropdown(false);
    setShowStatusDropdown(false);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilterDepartment('all');
    setFilterType('all');
    setFilterStatus('all');
    setShowDepartmentDropdown(false);
    setShowTypeDropdown(false);
    setShowStatusDropdown(false);
    setCurrentPage(1);
  };

  const hasActiveFilters = filterDepartment !== 'all' || filterType !== 'all' || filterStatus !== 'all';

  const handleDelete = (employee: Employee) => {
    setEmployeeToDelete(employee);
    setDeleteModalVisible(true);
  };

  const confirmDelete = async () => {
    if (!employeeToDelete) return;
    setDeleting(true);
    try {
      await employeesApi.delete(employeeToDelete.id);
      setFeedbackMessage({
        type: 'success',
        text: t('employees.deleteSuccess'),
      });
      fetchData();
      // Auto-hide feedback after 3 seconds
      setTimeout(() => setFeedbackMessage(null), 3000);
    } catch (err: any) {
      console.error('Failed to delete employee:', err);
      setFeedbackMessage({
        type: 'error',
        text: t('employees.deleteFailed'),
      });
      setTimeout(() => setFeedbackMessage(null), 3000);
    } finally {
      setDeleting(false);
      setDeleteModalVisible(false);
      setEmployeeToDelete(null);
    }
  };

  const getStatusLabel = (isActive: boolean) => isActive ? t('employees.active') : t('employees.inactive');
  const getStatusColor = (isActive: boolean) => isActive ? '#22C55E' : '#9CA3AF';
  const getTypeLabel = (type?: string) => type === 'manager' ? t('employees.manager') : t('employees.employee');

  const handleExportExcel = async () => {
    try {
      // Fetch all employees (without pagination) for export
      const allEmployeesData = await employeesApi.list({
        page_size: 1000,
        include_sso: 1,
        department_id: organizationId,
      });
      const allEmployees = allEmployeesData.results || [];

      if (allEmployees.length === 0) {
        setFeedbackMessage({
          type: 'error',
          text: t('employees.noDataToExport'),
        });
        setTimeout(() => setFeedbackMessage(null), 3000);
        return;
      }

      // Prepare data for Excel
      const headers = [
        t('employees.employeeNumber'),
        t('employees.nameArabic'),
        t('employees.nameEnglish'),
        t('employees.jobTitle'),
        t('employees.department'),
        t('employees.email'),
        t('employees.phone'),
        t('employees.status'),
        t('employees.performanceRate'),
        t('employees.hireDate'),
      ];

      const data = allEmployees.map((emp: Employee) => [
        emp.employee_number || '',
        emp.name_ar || '',
        emp.name_en || '',
        emp.position || '',
        emp.organizational_unit_name_ar || emp.organizational_unit_name || '',
        emp.email || '',
        emp.phone || '',
        emp.is_active ? t('employees.active') : t('employees.inactive'),
        `${emp.overall_achievement || 0}%`,
        emp.hire_date || '',
      ]);

      // Create worksheet
      const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);

      // Set column widths
      ws['!cols'] = [
        { wch: 12 },  // رقم الموظف
        { wch: 20 },  // الاسم بالعربي
        { wch: 20 },  // الاسم بالإنجليزي
        { wch: 18 },  // المسمى الوظيفي
        { wch: 20 },  // القسم
        { wch: 25 },  // البريد الإلكتروني
        { wch: 15 },  // رقم الهاتف
        { wch: 10 },  // الحالة
        { wch: 12 },  // نسبة الأداء
        { wch: 12 },  // تاريخ التعيين
      ];

      // Create workbook
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, t('employees.title'));

      // Generate file
      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });

      // Download file (web)
      if (Platform.OS === 'web') {
        const blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const date = new Date().toISOString().split('T')[0];
        link.download = `${t('employees.employeesList')}_${date}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }

      setFeedbackMessage({
        type: 'success',
        text: t('employees.exportSuccess', { count: allEmployees.length }),
      });
      setTimeout(() => setFeedbackMessage(null), 3000);
    } catch (error) {
      console.error('Export error:', error);
      setFeedbackMessage({
        type: 'error',
        text: t('employees.exportFailed'),
      });
      setTimeout(() => setFeedbackMessage(null), 3000);
    }
  };

  // Pagination controls
  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const getVisiblePages = () => {
    const pages: number[] = [];
    const maxVisible = 4;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    const end = Math.min(totalPages, start + maxVisible - 1);

    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  };

  // Full-page skeleton only on cold load (no cached data yet). Warm cache
  // and background refetches fall through to the regular render.
  if (loading && employees.length === 0) {
    return (
      <PageContainer>
        <Navbar />
        <GoalsSkeleton />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Navbar />
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        {/* Breadcrumb */}
        <Breadcrumb
          items={organizationId ? [
            { label: t('navigation.home') },
            { label: t('nav.organizations'), route: 'Organizations' },
            { label: organizationName || '', route: 'OrganizationDetail', params: { id: organizationId } },
            { label: t('navigation.humanCapital') },
          ] : [
            { label: t('navigation.home'), route: 'DashboardStack' },
            { label: t('navigation.humanCapital') },
          ]}
        />

        {/* Header Card */}
        <View style={[styles.headerCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
          <View style={styles.headerRow}>
            <Text style={[styles.pageTitle, { color: themeColors.textPrimary }]}>{t('navigation.humanCapital')}</Text>
            {!isViewOnly && (
              <View style={styles.actionButtons}>
                <TouchableOpacity
                  style={styles.addButton}
                  onPress={() => navigation.navigate('AddEmployee', { source: 'employees' })}
                >
                  <PlusIcon size={16} color="#FFFFFF" />
                  <Text style={styles.addButtonText}>{t('employees.addEmployee')}</Text>
                </TouchableOpacity>

                <TouchableOpacity
                  style={[styles.secondaryButton, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}
                  onPress={() => navigation.navigate('ImportEmployees')}
                >
                  <UploadIcon size={16} color={themeColors.textSecondary} />
                  <Text style={[styles.secondaryButtonText, { color: themeColors.textPrimary }]}>{t('employees.importList')}</Text>
                </TouchableOpacity>

                <TouchableOpacity
                  style={[styles.secondaryButton, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}
                  onPress={handleExportExcel}
                >
                  <DownloadIcon size={16} color={themeColors.textSecondary} />
                  <Text style={[styles.secondaryButtonText, { color: themeColors.textPrimary }]}>{t('employees.exportExcel')}</Text>
                </TouchableOpacity>
              </View>
            )}
          </View>

          {/* Filter Row */}
          <View style={styles.filterRow}>
          {hasActiveFilters && (
            <TouchableOpacity style={styles.clearFilterButton} onPress={clearFilters}>
              <XIcon size={14} color="#EF4444" />
              <Text style={styles.clearFilterText}>{t('filters.clearFilter')}</Text>
            </TouchableOpacity>
          )}

          <TouchableOpacity style={[styles.applyFilterBtn, { backgroundColor: themeColors.sectionBg }]} onPress={applyFilters}>
            <Text style={[styles.applyFilterBtnText, { color: themeColors.textPrimary }]}>{t('common.applyFilter')}</Text>
          </TouchableOpacity>

          {/* Status dropdown */}
          <View style={[styles.filterDropdownContainer, { zIndex: 30 }]}>
            <TouchableOpacity
              style={[styles.filterDropdown, { backgroundColor: themeColors.card, borderColor: filterStatus !== 'all' ? COLORS.primary : themeColors.cardBorder }]}
              onPress={() => {
                setShowStatusDropdown(!showStatusDropdown);
                setShowTypeDropdown(false);
                setShowDepartmentDropdown(false);
              }}
            >
              <ChevronDownIcon size={14} color={filterStatus !== 'all' ? COLORS.primary : themeColors.textSecondary} />
              <Text style={[styles.filterDropdownText, { color: filterStatus !== 'all' ? COLORS.primary : themeColors.textPrimary }]}>
                {filterStatus === 'all' ? t('filters.allStatuses') : filterStatus === 'active' ? t('employees.active') : t('employees.inactive')}
              </Text>
            </TouchableOpacity>
            {showStatusDropdown && (
              <View style={[styles.dropdownMenu, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
                {['all', 'active', 'inactive'].map((status, index, arr) => (
                  <TouchableOpacity
                    key={status}
                    style={[styles.dropdownItem, { borderBottomColor: themeColors.border }, index === arr.length - 1 && { borderBottomWidth: 0 }]}
                    onPress={() => {
                      setFilterStatus(status);
                      setShowStatusDropdown(false);
                    }}
                  >
                    <View style={styles.dropdownItemRow}>
                      {filterStatus === status && <CheckIcon size={16} color={COLORS.primary} />}
                      <Text style={[styles.dropdownItemText, { color: themeColors.textPrimary }, filterStatus === status && styles.dropdownItemTextActive]}>
                        {status === 'all' ? t('filters.allStatuses') : status === 'active' ? t('employees.active') : t('employees.inactive')}
                      </Text>
                    </View>
                  </TouchableOpacity>
                ))}
              </View>
            )}
          </View>

          {/* Type dropdown */}
          <View style={[styles.filterDropdownContainer, { zIndex: 20 }]}>
            <TouchableOpacity
              style={[styles.filterDropdown, { backgroundColor: themeColors.card, borderColor: filterType !== 'all' ? COLORS.primary : themeColors.cardBorder }]}
              onPress={() => {
                setShowTypeDropdown(!showTypeDropdown);
                setShowStatusDropdown(false);
                setShowDepartmentDropdown(false);
              }}
            >
              <ChevronDownIcon size={14} color={filterType !== 'all' ? COLORS.primary : themeColors.textSecondary} />
              <Text style={[styles.filterDropdownText, { color: filterType !== 'all' ? COLORS.primary : themeColors.textPrimary }]}>
                {filterType === 'all' ? t('filters.allTypes') : getTypeLabel(filterType)}
              </Text>
            </TouchableOpacity>
            {showTypeDropdown && (
              <View style={[styles.dropdownMenu, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
                {['all', 'employee', 'manager'].map((type, index, arr) => (
                  <TouchableOpacity
                    key={type}
                    style={[styles.dropdownItem, { borderBottomColor: themeColors.border }, index === arr.length - 1 && { borderBottomWidth: 0 }]}
                    onPress={() => {
                      setFilterType(type);
                      setShowTypeDropdown(false);
                    }}
                  >
                    <View style={styles.dropdownItemRow}>
                      {filterType === type && <CheckIcon size={16} color={COLORS.primary} />}
                      <Text style={[styles.dropdownItemText, { color: themeColors.textPrimary }, filterType === type && styles.dropdownItemTextActive]}>
                        {type === 'all' ? t('filters.allTypes') : getTypeLabel(type)}
                      </Text>
                    </View>
                  </TouchableOpacity>
                ))}
              </View>
            )}
          </View>

          {/* Department dropdown */}
          <View style={[styles.filterDropdownContainer, { zIndex: 10 }]}>
            <TouchableOpacity
              style={[styles.filterDropdown, { backgroundColor: themeColors.card, borderColor: filterDepartment !== 'all' ? COLORS.primary : themeColors.cardBorder }]}
              onPress={() => {
                setShowDepartmentDropdown(!showDepartmentDropdown);
                setShowStatusDropdown(false);
                setShowTypeDropdown(false);
              }}
            >
              <ChevronDownIcon size={14} color={filterDepartment !== 'all' ? COLORS.primary : themeColors.textSecondary} />
              <Text style={[styles.filterDropdownText, { color: filterDepartment !== 'all' ? COLORS.primary : themeColors.textPrimary }]} numberOfLines={1}>
                {filterDepartment === 'all' ? t('filters.allDepartments') : departments.find(d => d.id.toString() === filterDepartment)?.name || t('filters.allDepartments')}
              </Text>
            </TouchableOpacity>
            {showDepartmentDropdown && (
              <View style={[styles.dropdownMenu, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
                <ScrollView style={{ maxHeight: 200 }} nestedScrollEnabled showsVerticalScrollIndicator>
                  <TouchableOpacity
                    style={[styles.dropdownItem, { borderBottomColor: themeColors.border }]}
                    onPress={() => {
                      setFilterDepartment('all');
                      setShowDepartmentDropdown(false);
                    }}
                  >
                    <View style={styles.dropdownItemRow}>
                      {filterDepartment === 'all' && <CheckIcon size={16} color={COLORS.primary} />}
                      <Text style={[styles.dropdownItemText, { color: themeColors.textPrimary }, filterDepartment === 'all' && styles.dropdownItemTextActive]}>
                        {t('filters.allDepartments')}
                      </Text>
                    </View>
                  </TouchableOpacity>
                  {departments.map((dept, index) => (
                    <TouchableOpacity
                      key={dept.id}
                      style={[styles.dropdownItem, { borderBottomColor: themeColors.border }, index === departments.length - 1 && { borderBottomWidth: 0 }]}
                      onPress={() => {
                        setFilterDepartment(dept.id.toString());
                        setShowDepartmentDropdown(false);
                      }}
                    >
                      <View style={styles.dropdownItemRow}>
                        {filterDepartment === dept.id.toString() && <CheckIcon size={16} color={COLORS.primary} />}
                        <Text style={[styles.dropdownItemText, { color: themeColors.textPrimary }, filterDepartment === dept.id.toString() && styles.dropdownItemTextActive]} numberOfLines={1}>
                          {dept.name}
                        </Text>
                      </View>
                    </TouchableOpacity>
                  ))}
                </ScrollView>
              </View>
            )}
          </View>
        </View>
        </View>

        {/* Table - No card wrapper, directly on background */}
        <View style={styles.tableContainer}>
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={Platform.OS === 'web'}
            contentContainerStyle={{ flexGrow: 1 }}
          >
            <View style={{ flex: 1, minWidth: '100%' }}>
              {/* Table Header */}
              <View style={[styles.tableHeader, { backgroundColor: themeColors.tableHeader }]}>
                {/* Actions column - no header text */}
                <View style={[styles.tableCell, styles.actionsCell]} />
                <View style={[styles.tableCell, styles.statusCell]}>
                  <Text style={[styles.tableHeaderText, { color: themeColors.textPrimary }]}>{t('employees.status')}</Text>
                </View>
                <View style={[styles.tableCell, styles.performanceCell]}>
                  <Text style={[styles.tableHeaderText, { color: themeColors.textPrimary }]}>{t('employees.performance')}</Text>
                </View>
                <View style={[styles.tableCell, styles.typeCell]}>
                  <Text style={[styles.tableHeaderText, { color: themeColors.textPrimary }]}>{t('employees.type')}</Text>
                </View>
                <View style={[styles.tableCell, styles.phoneCell]}>
                  <Text style={[styles.tableHeaderText, { color: themeColors.textPrimary }]}>{t('employees.phone')}</Text>
                </View>
                <View style={[styles.tableCell, styles.emailCell]}>
                  <Text style={[styles.tableHeaderText, { color: themeColors.textPrimary }]}>{t('employees.email')}</Text>
                </View>
                <View style={[styles.tableCell, styles.departmentCell]}>
                  <Text style={[styles.tableHeaderText, { color: themeColors.textPrimary }]}>{t('employees.department')}</Text>
                </View>
                <View style={[styles.tableCell, styles.nameCell]}>
                  <Text style={[styles.tableHeaderText, { color: themeColors.textPrimary }]}>{t('employees.userName')}</Text>
                </View>
                <View style={[styles.tableCell, styles.accountCell]}>
                  <Text style={[styles.tableHeaderText, { color: themeColors.textPrimary }]}>{t('employees.accountName')}</Text>
                </View>
              </View>

              {/* Table Rows */}
              {employees.length > 0 ? (
                employees.map((employee, index) => (
                  <View
                    key={employee.id}
                    style={[
                      styles.tableRow,
                      { backgroundColor: index % 2 === 0 ? themeColors.tableRow : themeColors.tableRowAlt },
                    ]}
                  >
                    {/* Actions */}
                    <View style={[styles.tableCell, styles.actionsCell]}>
                      <View style={styles.actionIcons}>
                        {/* SSO directory rows have no local Employee record — read-only */}
                        {!isViewOnly && employee.source !== 'sso' && (
                          <>
                            <TouchableOpacity onPress={() => handleDelete(employee)} style={styles.iconButton}>
                              <TrashIcon size={18} color="#EF4444" />
                            </TouchableOpacity>
                            <TouchableOpacity
                              onPress={() => navigation.navigate('AddEmployee', { employeeId: employee.id, source: 'employees' })}
                              style={styles.iconButton}
                            >
                              <EditIcon size={18} color={themeColors.textSecondary} />
                            </TouchableOpacity>
                          </>
                        )}
                        {employee.source !== 'sso' && (
                          <TouchableOpacity
                            onPress={() => navigation.navigate('EmployeeDetail', { id: employee.id, source: 'employees' })}
                            style={styles.iconButton}
                          >
                            <EyeIcon size={18} color={themeColors.textSecondary} />
                          </TouchableOpacity>
                        )}
                      </View>
                    </View>

                    {/* Status */}
                    <View style={[styles.tableCell, styles.statusCell]}>
                      <View style={[styles.statusBadge, { backgroundColor: getStatusColor(employee.is_active ?? false) }]}>
                        <Text style={styles.statusBadgeText}>{getStatusLabel(employee.is_active ?? false)}</Text>
                      </View>
                    </View>

                    {/* Performance */}
                    <View style={[styles.tableCell, styles.performanceCell]}>
                      <View style={styles.performanceContainer}>
                        <Text style={[styles.performanceText, { color: themeColors.textPrimary }]}>
                          {employee.overall_achievement || 0} %
                        </Text>
                        <View style={[styles.performanceCircle, { borderColor: '#22C55E' }]}>
                          <View style={[styles.performanceInner, { backgroundColor: '#22C55E' }]} />
                        </View>
                      </View>
                    </View>

                    {/* Type */}
                    <View style={[styles.tableCell, styles.typeCell]}>
                      <View style={styles.typeBadge}>
                        <Text style={styles.typeBadgeText}>{t(`employees.userTypes.${employee.user_type || 'employee'}`)}</Text>
                      </View>
                    </View>

                    {/* Phone */}
                    <View style={[styles.tableCell, styles.phoneCell]}>
                      <Text style={[styles.cellText, { color: themeColors.textPrimary }]}>{employee.phone || '-'}</Text>
                    </View>

                    {/* Email */}
                    <View style={[styles.tableCell, styles.emailCell]}>
                      <Text style={[styles.cellText, { color: themeColors.textPrimary }]} numberOfLines={1}>
                        {employee.email || '-'}
                      </Text>
                    </View>

                    {/* Department */}
                    <View style={[styles.tableCell, styles.departmentCell]}>
                      <Text style={[styles.cellText, { color: themeColors.textPrimary }]} numberOfLines={1}>
                        {employee.organizational_unit_name_ar || employee.organizational_unit_name || '-'}
                      </Text>
                    </View>

                    {/* Name */}
                    <View style={[styles.tableCell, styles.nameCell]}>
                      <Text style={[styles.cellText, { color: themeColors.textPrimary }]}>{employee.name_ar}</Text>
                    </View>

                    {/* Account */}
                    <View style={[styles.tableCell, styles.accountCell]}>
                      <Text style={[styles.cellText, { color: themeColors.textPrimary }]}>
                        {employee.employee_number || `emp${employee.id}`}
                      </Text>
                    </View>
                  </View>
                ))
              ) : (
                <View style={styles.emptyRow}>
                  <Text style={[styles.emptyText, { color: themeColors.textSecondary }]}>{t('employees.noEmployees')}</Text>
                </View>
              )}
            </View>
          </ScrollView>
        </View>

        {/* Pagination */}
        <View style={[styles.paginationContainer, { flexDirection: rtlRow }]}>
          <Text style={[styles.paginationInfo, { color: themeColors.textSecondary }]}>
            {t('pagination.showing', { start: Math.min((currentPage - 1) * pageSize + 1, totalCount), end: Math.min(currentPage * pageSize, totalCount), total: totalCount })}
          </Text>

          <View style={[styles.paginationControls, { flexDirection: rtlRow }]}>
            <TouchableOpacity
              style={[styles.paginationButton, currentPage === 1 && styles.paginationButtonDisabled]}
              onPress={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
            >
              <Text style={[styles.paginationButtonText, { color: currentPage === 1 ? '#9CA3AF' : themeColors.textPrimary }]}>{t('common.previous')}</Text>
              <ChevronRightIcon size={16} color={currentPage === 1 ? '#9CA3AF' : themeColors.textPrimary} />
            </TouchableOpacity>

            <View style={[styles.pageNumbers, { flexDirection: rtlRow }]}>
              {getVisiblePages().map((page) => (
                <TouchableOpacity
                  key={page}
                  style={[styles.pageNumber, currentPage === page && styles.pageNumberActive]}
                  onPress={() => goToPage(page)}
                >
                  <Text style={[styles.pageNumberText, currentPage === page && styles.pageNumberTextActive]}>
                    {page}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>

            <TouchableOpacity
              style={[styles.paginationButton, currentPage === totalPages && styles.paginationButtonDisabled]}
              onPress={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              <ChevronLeftIcon size={16} color={currentPage === totalPages ? '#9CA3AF' : themeColors.textPrimary} />
              <Text style={[styles.paginationButtonText, { color: currentPage === totalPages ? '#9CA3AF' : themeColors.textPrimary }]}>{t('common.next')}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </ScrollView>

      {/* Feedback Message */}
      {feedbackMessage && (
        <View style={[
          styles.feedbackMessage,
          feedbackMessage.type === 'success' && styles.feedbackSuccess,
          feedbackMessage.type === 'info' && styles.feedbackInfo,
          feedbackMessage.type === 'error' && styles.feedbackError,
        ]}>
          <Text style={styles.feedbackText}>{feedbackMessage.text}</Text>
        </View>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        visible={deleteModalVisible}
        transparent
        animationType="fade"
        onRequestClose={() => setDeleteModalVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>{t('common.confirmDelete')}</Text>
            <Text style={styles.modalMessage}>
              {t('employees.deleteConfirm', { name: employeeToDelete?.name_ar })}
            </Text>
            <View style={styles.modalButtons}>
              <TouchableOpacity
                style={styles.modalCancelButton}
                onPress={() => setDeleteModalVisible(false)}
              >
                <Text style={styles.modalCancelButtonText}>{t('common.cancel')}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.modalDeleteButton, deleting && styles.modalButtonDisabled]}
                onPress={confirmDelete}
                disabled={deleting}
              >
                {deleting ? (
                  <ActivityIndicator size="small" color="#FFFFFF" />
                ) : (
                  <Text style={styles.modalDeleteButtonText}>{t('common.delete')}</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </PageContainer>
  );
}

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: 24,
    paddingBottom: 40,
  },
  tableContainer: {
    width: '100%',
    marginBottom: 20,
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 12,
  },
  loadingText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
  },
  headerCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    paddingHorizontal: 20,
    paddingVertical: 16,
    marginBottom: 24,
    shadowColor: '#000000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 3,
    elevation: 2,
    zIndex: 100,
    position: 'relative',
  },
  headerRow: {
    flexDirection: 'row-reverse',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 16,
  },
  pageTitle: {
    fontSize: 24,
    fontFamily: FONTS.bold,
    color: '#0F172A',
  },
  actionButtons: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#A9211F',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
    gap: 8,
  },
  addButtonText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontFamily: FONTS.medium,
  },
  secondaryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
    gap: 8,
  },
  secondaryButtonText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
  },
  filterRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 16,
    marginTop: 16,
    position: 'relative',
    zIndex: 1000,
  },
  applyFilterBtn: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    backgroundColor: '#F8FAFC',
  },
  applyFilterBtnText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
  },
  clearFilterButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#FCA5A5',
    backgroundColor: '#FEF2F2',
    gap: 6,
  },
  clearFilterText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#EF4444',
  },
  filterDropdownContainer: {
    flex: 1,
    maxWidth: 200,
    position: 'relative',
  },
  filterDropdown: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
  },
  filterDropdownText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    textAlign: 'right',
  },
  dropdownMenu: {
    position: 'absolute',
    top: 44,
    left: 0,
    right: 0,
    borderRadius: 8,
    borderWidth: 1,
    shadowColor: '#000000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 10,
    zIndex: 9999,
    overflow: 'hidden',
  },
  dropdownItem: {
    paddingHorizontal: 12,
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  dropdownItemRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
  },
  dropdownItemText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    textAlign: 'right',
  },
  dropdownItemTextActive: {
    color: COLORS.primary,
    fontFamily: FONTS.medium,
  },
  tableHeader: {
    flexDirection: 'row',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
    flex: 1,
  },
  tableHeaderText: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    textAlign: 'right',
  },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#F1F5F9',
    flex: 1,
  },
  tableCell: {
    justifyContent: 'center',
    alignItems: 'flex-end',
    paddingHorizontal: 8,
  },
  actionsCell: {
    width: 100,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 8,
  },
  statusCell: {
    width: 80,
    alignItems: 'center',
  },
  performanceCell: {
    width: 90,
  },
  typeCell: {
    width: 120,
    minWidth: 120,
  },
  phoneCell: {
    width: 130,
  },
  emailCell: {
    flex: 1,
    minWidth: 200,
  },
  departmentCell: {
    flex: 1,
    minWidth: 180,
  },
  nameCell: {
    flex: 1,
    minWidth: 150,
  },
  accountCell: {
    width: 120,
  },
  cellText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    textAlign: 'right',
  },
  actionIcons: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  iconButton: {
    padding: 4,
  },
  statusBadge: {
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 4,
  },
  statusBadgeText: {
    color: '#FFFFFF',
    fontSize: 12,
    fontFamily: FONTS.medium,
  },
  performanceContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  performanceText: {
    fontSize: 14,
    fontFamily: FONTS.medium,
  },
  performanceCircle: {
    width: 24,
    height: 24,
    borderRadius: 12,
    borderWidth: 3,
    justifyContent: 'center',
    alignItems: 'center',
  },
  performanceInner: {
    width: 10,
    height: 10,
    borderRadius: 5,
  },
  typeBadge: {
    backgroundColor: '#3B82F6',
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 4,
  },
  typeBadgeText: {
    color: '#FFFFFF',
    fontSize: 12,
    fontFamily: FONTS.medium,
  },
  emptyRow: {
    padding: 40,
    alignItems: 'center',
  },
  emptyText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
  },
  paginationContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 16,
  },
  paginationControls: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  paginationButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    gap: 4,
  },
  paginationButtonDisabled: {
    opacity: 0.5,
  },
  paginationButtonText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
  },
  pageNumbers: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  pageNumber: {
    width: 32,
    height: 32,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
  },
  pageNumberActive: {
    backgroundColor: '#F1F5F9',
  },
  pageNumberText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#6B7280',
  },
  pageNumberTextActive: {
    color: '#0F172A',
    fontFamily: FONTS.medium,
  },
  paginationInfo: {
    fontSize: 14,
    fontFamily: FONTS.regular,
  },
  // Modal styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 24,
    width: '90%',
    maxWidth: 400,
    shadowColor: '#000000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 10,
  },
  modalTitle: {
    fontSize: 18,
    fontFamily: FONTS.bold,
    color: '#0F172A',
    textAlign: 'right',
    marginBottom: 12,
  },
  modalMessage: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#64748B',
    textAlign: 'right',
    marginBottom: 24,
    lineHeight: 22,
  },
  modalButtons: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    gap: 12,
  },
  modalCancelButton: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    backgroundColor: '#FFFFFF',
    alignItems: 'center',
  },
  modalCancelButtonText: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#64748B',
  },
  modalDeleteButton: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    backgroundColor: '#EF4444',
    alignItems: 'center',
  },
  modalButtonDisabled: {
    opacity: 0.6,
  },
  modalDeleteButtonText: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#FFFFFF',
  },
  // Feedback message styles
  feedbackMessage: {
    position: 'absolute',
    bottom: 24,
    left: 24,
    right: 24,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    alignItems: 'center',
    zIndex: 1000,
  },
  feedbackSuccess: {
    backgroundColor: '#22C55E',
  },
  feedbackInfo: {
    backgroundColor: '#3B82F6',
  },
  feedbackError: {
    backgroundColor: '#EF4444',
  },
  feedbackText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontFamily: FONTS.medium,
    textAlign: 'center',
  },
});
